import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import handleError from "@composables/handleError";
import useMyFetch from "@stores/useMyFetch";
import { useSurveyStore } from "@stores/survey/survey";
import { getClientId } from "@utils/clientId";
import type {
  Grid,
  GridCellStatus,
  GridCitation,
  GridColumn,
  GridSummary,
  GridSummarySegment,
  GridType,
  Interview,
} from "@/types";
import { captureEvent } from "@/lib/analytics";

interface Population {
  id: number;
  token: string;
  name: string;
  color: string | null;
  filters: Record<string, Record<string, boolean>>;
}

export const IN_MEMORY_TOKEN = "default";

export const useGridsStore = defineStore("grids", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const grids = ref<Grid[]>([]);
  const currentGridToken = ref<string | null>(null);
  const candidates = ref<Interview[]>([]);
  const populations = ref<Population[]>([]);
  const loading = ref(false);

  const basePath = () => `surveys/${surveyToken.value}/grids`;

  const currentGrid = computed(
    () => grids.value.find((grid) => grid.token === currentGridToken.value) ?? grids.value[0] ?? null,
  );

  const populationGrids = computed(() => grids.value.filter((grid) => grid.grid_type === "population"));

  const respondentGrids = computed(
    () => grids.value.filter((grid) => grid.grid_type === "respondent" && !grid.default_grid),
  );

  const defaultGrid = computed(() => grids.value.find((grid) => grid.default_grid) ?? null);

  const selectGrid = (token: string) => {
    currentGridToken.value = token;
  };

  const fetch = async () => {
    try {
      loading.value = true;
      const { response, data } = await useMyFetch(basePath()).json<Grid[]>();
      if (!response.value?.ok) return;

      grids.value = data.value ?? [];
      if (!grids.value.some((grid) => grid.token === currentGridToken.value)) {
        currentGridToken.value = grids.value[0]?.token ?? null;
      }
    } catch (err) {
      handleError(err);
    } finally {
      loading.value = false;
    }
  };

  const create = async (name: string, gridType: GridType) => {
    try {
      const { response, data } = await useMyFetch(basePath()).post({ grid: { name, grid_type: gridType } }).json<Grid>();
      if (!response.value?.ok || !data.value) return null;

      grids.value.push(data.value);
      currentGridToken.value = data.value.token;
      return data.value;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const rename = async (token: string, name: string) => {
    try {
      const { response, data } = await useMyFetch(`${basePath()}/${token}`).patch({ grid: { name } }).json<Grid>();
      if (!response.value?.ok || !data.value) return;

      replace(data.value);
    } catch (err) {
      handleError(err);
    }
  };

  const duplicate = async (token: string, name?: string) => {
    try {
      const { response, data } = await useMyFetch(`${basePath()}/${token}/duplicate`).post({ name }).json<Grid>();
      if (!response.value?.ok || !data.value) return null;

      grids.value.push(data.value);
      currentGridToken.value = data.value.token;
      return data.value;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const remove = async (token: string) => {
    try {
      const { response } = await useMyFetch(`${basePath()}/${token}`).delete();
      if (!response.value?.ok) return;

      grids.value = grids.value.filter((grid) => grid.token !== token);
      if (currentGridToken.value === token) currentGridToken.value = grids.value[0]?.token ?? null;
    } catch (err) {
      handleError(err);
    }
  };

  const addColumn = async (questionId: number) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/columns`).post({
        column: { question_id: questionId },
      });
      if (!response.value?.ok) return;
      captureEvent("interviews_columns_updated", { selected: true });
      await fetch();
    } catch (err) {
      handleError(err);
    }
  };

  const askQuestion = async (prompt: string) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response, data } = await useMyFetch(`${basePath()}/${grid.token}/columns/ask`)
        .post({ prompt, client_id: getClientId() })
        .json<GridColumn>();
      if (!response.value?.ok || !data.value) return;
      if (grid.token === IN_MEMORY_TOKEN) await fetch();
      else if (!columnById(data.value.id)) grid.columns.push(data.value);
    } catch (err) {
      handleError(err);
    }
  };

  const STALE_AFTER_MS = 10_000;
  let refetching: Promise<void> | null = null;
  let lastUpdateAt = Date.now();

  const refetchOnce = () => {
    refetching ??= fetch().finally(() => {
      refetching = null;
      lastUpdateAt = Date.now();
    });
  };

  const hasPendingCells = () =>
    (currentGrid.value?.columns ?? []).some((column) =>
      column.cells.some((cell) => cell.status === "pending" || cell.status === "streaming"),
    );

  const refreshIfStale = () => {
    if (hasPendingCells() && Date.now() - lastUpdateAt > STALE_AFTER_MS) refetchOnce();
  };

  const columnById = (id: number) =>
    grids.value.flatMap((grid) => grid.columns ?? []).find((column) => column.id === id) ?? null;

  const gridOfColumn = (columnId: number) =>
    grids.value.find((grid) => (grid.columns ?? []).some((column) => column.id === columnId)) ?? null;

  const applySummaryUpdate = (payload: {
    grid_summary_id: number;
    grid_column_id: number;
    status: GridSummary["status"];
    segments: GridSummarySegment[];
  }) => {
    const grid = gridOfColumn(payload.grid_column_id);
    if (!grid) return refetchOnce();

    const summary = grid.summaries.find((item) => item.grid_column_id === payload.grid_column_id);
    if (!summary) {
      const { grid_summary_id: id, grid_column_id, status, segments } = payload;
      grid.summaries.push({ id, grid_column_id, status, segments: segments ?? [], outdated: false });
      return;
    }

    summary.status = payload.status;
    summary.segments = payload.segments ?? [];
    if (payload.status === "done") summary.outdated = false;
  };

  const applyCellUpdate = (payload: {
    grid_summary_id?: number;
    grid_column_id: number;
    survey_customer_id?: number | null;
    search_group_id?: number | null;
    status: GridCellStatus;
    answer: string | null;
    citations: GridCitation[];
  }) => {
    lastUpdateAt = Date.now();
    if (payload.grid_summary_id) return applySummaryUpdate(payload as unknown as Parameters<typeof applySummaryUpdate>[0]);

    const column = columnById(payload.grid_column_id);
    if (!column) {
      refetchOnce();
      return;
    }

    const cell = column.cells.find((item) =>
      payload.search_group_id
        ? item.search_group_id === payload.search_group_id
        : item.survey_customer_id === payload.survey_customer_id,
    );
    if (!cell) return;

    cell.status = payload.status;
    cell.answer = payload.answer;
    cell.citations = payload.citations ?? [];
  };

  const cellFor = (columnId: number, surveyCustomerId: number) =>
    columnById(columnId)?.cells.find((cell) => cell.survey_customer_id === surveyCustomerId) ?? null;

  const hasColumn = (questionId: number) =>
    (currentGrid.value?.columns ?? []).some((column) => column.question_id === questionId);

  const ensureColumn = async (questionId: number) => {
    if (!hasColumn(questionId)) await addColumn(questionId);
  };

  const materialize = async () => {
    const grid = currentGrid.value;
    if (grid?.token !== IN_MEMORY_TOKEN) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/materialize`).post();
      if (response.value?.ok) await fetch();
    } catch (err) {
      handleError(err);
    }
  };

  const removeColumn = async (id: number) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/columns/${id}`).delete();
      if (!response.value?.ok) return;
      grid.columns = grid.columns.filter((column) => column.id !== id);
      captureEvent("interviews_columns_updated", { selected: false });
    } catch (err) {
      handleError(err);
    }
  };

  const reorder = async (kind: "rows" | "columns", positions: { id: number; position: number }[]) => {
    const grid = currentGrid.value;
    if (!grid) return;

    const items: { id: number; position: number }[] = grid[kind];
    positions.forEach(({ id, position }) => {
      const item = items.find((entry) => entry.id === id);
      if (item) item.position = position;
    });
    items.sort((a, b) => a.position - b.position);

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/${kind}/reorder`).patch({ positions });
      if (!response.value?.ok) await fetch();
    } catch (err) {
      handleError(err);
      await fetch();
    }
  };

  const addPopulations = async (tokens: string[]) => {
    const grid = currentGrid.value;
    if (!grid || !tokens.length) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/rows`).post({
        search_group_tokens: tokens,
        client_id: getClientId(),
      });
      if (response.value?.ok) await fetch();
    } catch (err) {
      handleError(err);
    }
  };

  const addRows = async (surveyCustomerIds: number[], populationTokens: string[]) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/rows`).post({
        survey_customer_ids: surveyCustomerIds,
        search_group_tokens: populationTokens,
        client_id: getClientId(),
      });
      if (response.value?.ok) await fetch();
    } catch (err) {
      handleError(err);
    }
  };

  const refreshRow = async (id: number) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/rows/${id}/refresh`).post({
        client_id: getClientId(),
      });
      if (response.value?.ok) await fetch();
    } catch (err) {
      handleError(err);
    }
  };

  const refreshSummary = async (columnId: number) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      await useMyFetch(`${basePath()}/${grid.token}/columns/${columnId}/refresh_summary`).post({
        client_id: getClientId(),
      });
    } catch (err) {
      handleError(err);
    }
  };

  const removeRow = async (id: number) => {
    const grid = currentGrid.value;
    if (!grid) return;

    try {
      const { response } = await useMyFetch(`${basePath()}/${grid.token}/rows/${id}`).delete();
      if (response.value?.ok) grid.rows = grid.rows.filter((row) => row.id !== id);
    } catch (err) {
      handleError(err);
    }
  };

  const searchCandidates = async (query: string) => {
    try {
      const { response, data } = await useMyFetch(`surveys/${surveyToken.value}/grids/default/rows/search`)
        .post({ search_bar_input: query, page: 1, per_page: 50 })
        .json<{ interviews: Interview[] }>();
      if (response.value?.ok) candidates.value = data.value?.interviews ?? [];
    } catch (err) {
      handleError(err);
    }
  };

  const fetchPopulations = async () => {
    try {
      const { response, data } = await useMyFetch(`surveys/${surveyToken.value}/search_groups`).json<Population[]>();
      if (response.value?.ok) populations.value = data.value ?? [];
    } catch (err) {
      handleError(err);
    }
  };

  const replace = (grid: Grid) => {
    const index = grids.value.findIndex((item) => item.token === grid.token);
    if (index >= 0) grids.value[index] = grid;
  };

  const reset = () => {
    grids.value = [];
    currentGridToken.value = null;
    candidates.value = [];
    populations.value = [];
  };

  return {
    refreshIfStale,
    reset,
    grids,
    candidates,
    populations,
    loading,
    currentGrid,
    currentGridToken,
    defaultGrid,
    populationGrids,
    respondentGrids,
    selectGrid,
    fetch,
    create,
    rename,
    duplicate,
    remove,
    addColumn,
    materialize,
    askQuestion,
    applyCellUpdate,
    cellFor,
    ensureColumn,
    hasColumn,
    removeColumn,
    reorder,
    addRows,
    addPopulations,
    refreshRow,
    refreshSummary,
    removeRow,
    searchCandidates,
    fetchPopulations,
  };
});
